import { useTranslation } from 'react-i18next';
import { BlindStateDirectionInternal, GateEventTypeInternal } from '../../../../../data-access/gql-types/graphql';
import {
  BlindStateTask,
  DimStateTask,
  GatePartialControlModeStateTask,
  GateDailyModeStateTask,
  GateStateTask,
  OnOffStateTask,
  RGBWStateTask,
} from '../../../../../types/actions';
import { DeviceTaskParams, GateActionInternal, SceneTaskType, Task, TaskVariant } from '../types';

export const useActionDetailsPresentation = () => {
  const { t } = useTranslation('action');

  const getTaskState = (task: Task) => {
    switch (task.taskType) {
      case SceneTaskType.ChannelState: {
        const deviceParams = task.taskParams as DeviceTaskParams;

        switch (deviceParams.variant) {
          case TaskVariant.OnOff: {
            return t(`action.create.tasks.states.${(deviceParams.state as OnOffStateTask).isOn ? 'on' : 'off'}`);
          }
          case TaskVariant.Dim: {
            return `${t('action.create.tasks.states.brightness')} ${(deviceParams.state as DimStateTask).brightness}%`;
          }
          case TaskVariant.SetRGBW: {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span>{`${t('action.create.tasks.states.color')}: `}</span>
                <div
                  style={{
                    marginLeft: 6,
                    marginRight: 6,
                    height: 12,
                    width: 12,
                    borderRadius: '50%',
                    border: '1px solid #cbcbd1',
                    background: `rgb(${(deviceParams.state as RGBWStateTask).R},${
                      (deviceParams.state as RGBWStateTask).G
                    },${(deviceParams.state as RGBWStateTask).B})`,
                  }}
                />
              </div>
            );
          }
          case TaskVariant.SetBlind: {
            return (deviceParams.state as BlindStateTask<BlindStateDirectionInternal>).action ===
              BlindStateDirectionInternal.Unknown
              ? `${(deviceParams.state as BlindStateTask<BlindStateDirectionInternal>).position}%`
              : `${t(
                  `action.create.tasks.states.${(
                    deviceParams.state as BlindStateTask<BlindStateDirectionInternal>
                  ).action.toLowerCase()}`,
                )}`;
          }
          case TaskVariant.SetGate: {
            if ((deviceParams.state as GateStateTask<GateActionInternal>).action === GateActionInternal.Tilt) {
              return t('action.create.tasks.states.ventilation');
            }

            return (deviceParams.state as GateStateTask<GateActionInternal>).action === GateActionInternal.Unknown
              ? `${(deviceParams.state as GateStateTask<GateActionInternal>).position}%`
              : `${t(
                  `action.create.tasks.states.${(
                    deviceParams.state as GateStateTask<GateActionInternal>
                  ).action.toLowerCase()}`,
                )}`;
          }
          case TaskVariant.SetGateDailyMode: {
            const action = (deviceParams.state as GateDailyModeStateTask<GateEventTypeInternal>).action;

            return `${t('action.create.tasks.states.dailyMode')}: ${t(
              `action.create.tasks.states.${action === GateEventTypeInternal.SetDailyModeOn ? 'on' : 'off'}`,
            ).toLowerCase()}`;
          }
          case TaskVariant.SetGatePartialControlMode: {
            const action = (deviceParams.state as GatePartialControlModeStateTask<GateEventTypeInternal>).action;

            return `${t('action.create.tasks.states.partialControlMode')}: ${t(
              `action.create.tasks.states.${action === GateEventTypeInternal.SetPartialControlModeOn ? 'on' : 'off'}`,
            ).toLowerCase()}`;
          }
          case TaskVariant.SetVentilation: {
            return t('action.create.tasks.states.ventilation');
          }
          default: {
            return '';
          }
        }
      }
    }
  };

  return { getTaskState };
};
